import { useRef, useEffect } from 'react';
import { motion, useReducedMotion } from 'framer-motion';
import { Link } from 'react-router-dom';
import Stamp from './Stamp';
import Countdown from './Countdown';

const HERO_VIDEO_URL = process.env.REACT_APP_HERO_VIDEO_URL || 'https://siff1.s3.us-east-1.amazonaws.com/Synema+Fest+Sizzle+2.mp4';

export default function Hero() {
  const prefersReducedMotion = useReducedMotion();
  const videoRef = useRef(null);

  useEffect(() => {
    const video = videoRef.current;
    if (!video) return;

    const tryPlay = () => {
      video.muted = true;
      video.play().catch(() => {
        const onInteract = () => {
          video.play().catch(() => {});
          document.removeEventListener('touchstart', onInteract);
          document.removeEventListener('click', onInteract);
        };
        document.addEventListener('touchstart', onInteract, { once: true });
        document.addEventListener('click', onInteract, { once: true });
      });
    };

    if (video.readyState >= 2) {
      tryPlay();
    } else {
      video.addEventListener('loadeddata', tryPlay, { once: true });
    }
  }, []);

  const fadeUp = (delay = 0) =>
    prefersReducedMotion
      ? {}
      : {
          initial: { opacity: 0, y: 20 },
          animate: { opacity: 1, y: 0 },
          transition: { duration: 0.7, delay, ease: 'easeOut' },
        };

  return (
    <section className="relative min-h-screen flex items-center justify-center overflow-hidden">
      {/* Background video */}
      <div className="absolute inset-0 z-0">
        <video
          ref={videoRef}
          className="absolute top-1/2 left-1/2 min-w-full min-h-full w-auto h-auto -translate-x-1/2 -translate-y-1/2 object-cover"
          src={HERO_VIDEO_URL}
          autoPlay
          muted
          defaultMuted
          loop
          playsInline
          webkit-playsinline="true"
          x5-playsinline="true"
          preload="auto"
          disableRemotePlayback
          disablePictureInPicture
        />
        <div
          className="absolute inset-0"
          style={{
            background: 'linear-gradient(to bottom, rgba(14,14,18,0.65) 0%, rgba(14,14,18,0.45) 40%, rgba(14,14,18,0.7) 100%)',
          }}
        />
      </div>

      {/* Radial gradient accents */}
      <div
        className="absolute inset-0 z-[1]"
        style={{
          background:
            'radial-gradient(ellipse 60% 50% at 30% 40%, rgba(42,191,163,0.10) 0%, transparent 70%), ' +
            'radial-gradient(ellipse 50% 50% at 70% 60%, rgba(232,114,28,0.08) 0%, transparent 70%)',
        }}
      />

      {/* Grain texture overlay */}
      <div className="grain-overlay absolute inset-0 z-[2]" />

      {/* Content */}
      <div className="relative z-10 flex flex-col items-center text-center px-6 pt-28 pb-16 w-full max-w-5xl mx-auto">
        {/* Title */}
        <motion.h1
          {...fadeUp(0)}
          className="font-title hero-title-gradient drop-shadow-lg"
          style={{ fontSize: 'clamp(5rem, 14vw, 11rem)', lineHeight: 0.9, letterSpacing: '0.05em' }}
        >
          SYNEMA
        </motion.h1>

        {/* Subtitle */}
        <motion.p
          {...fadeUp(0.1)}
          className="font-display tracking-[0.2em] mt-2 drop-shadow-md"
          style={{
            fontSize: 'clamp(1.2rem, 3vw, 2rem)',
            color: 'rgba(245,245,240,0.55)',
          }}
        >
          Interactive Film Festival
        </motion.p>

        {/* Eyebrow */}
        <motion.p
          {...fadeUp(0.2)}
          className="font-mono uppercase tracking-[0.25em] text-teal mt-6"
          style={{ fontSize: '0.72rem' }}
        >
          Inaugural Edition &middot; January 21&ndash;24, 2027 &middot; Worldwide
        </motion.p>

        {/* Countdown */}
        <motion.div {...fadeUp(0.3)}>
          <Countdown />
        </motion.div>

        {/* Tagline */}
        <motion.p
          {...fadeUp(0.4)}
          className="mt-8 max-w-[520px] font-light text-[1.05rem] leading-[1.7]"
          style={{ color: 'rgba(245,245,240,0.7)' }}
        >
          The world&rsquo;s first communal streaming film festival. Your film
          doesn&rsquo;t screen to 200 people in one city &mdash; it screens to
          thousands worldwide, simultaneously, in a watch party.
        </motion.p>

        {/* CTAs */}
        <motion.div
          {...fadeUp(0.55)}
          className="mt-8 flex flex-wrap justify-center gap-4"
        >
          <Link
            to="/submit"
            className="btn-gradient inline-block rounded-[2px] px-8 py-3.5 text-[0.82rem] font-semibold tracking-[0.08em] uppercase text-dark no-underline hover:opacity-90 transition-all hover:-translate-y-[1px]"
          >
            Submit Your Film
          </Link>
          <Link
            to="/tickets"
            className="inline-block rounded-[2px] px-8 py-3.5 text-[0.82rem] font-medium tracking-[0.08em] uppercase text-teal no-underline transition-all hover:bg-teal hover:text-dark border border-teal"
          >
            Get Tickets
          </Link>
          <Link
            to="/sponsors"
            className="inline-block rounded-[2px] px-8 py-3.5 text-[0.82rem] font-semibold tracking-[0.08em] uppercase text-dark no-underline hover:opacity-90 transition-all hover:-translate-y-[1px] bg-gold"
          >
            Become a Sponsor
          </Link>
        </motion.div>

        {/* Stamp */}
        <motion.div {...fadeUp(0.65)} className="mt-10">
          <Stamp />
        </motion.div>
      </div>
    </section>
  );
}
